import Button from '../components/atoms/Button';
import IconButton from '../components/atoms/IconButton';

// Icons for demonstration
const PlusIcon = () => (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
  </svg>
);

const HeartIcon = () => (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
  </svg>
);

const SendIcon = () => (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
  </svg>
);

const EditIcon = () => (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
  </svg>
);

export function HoverDemo() {
  return (
    <div className="p-8 space-y-8 bg-gray-50 min-h-screen">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl font-bold text-gray-900 mb-8 text-center">
          Button Hover Effects Demo
        </h1>
        
        <div className="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-blue-800 text-center">
            <strong>💡 Hover over the buttons below to see the interactive effects!</strong>
          </p>
        </div>

        {/* Regular Button Hover Effects */}
        <section className="bg-white rounded-xl p-8 shadow-sm space-y-8">
          <h2 className="text-2xl font-semibold text-gray-800">Regular Button Hover Effects</h2>
          
          <div className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-700">Primary Buttons</h3>
              <div className="flex gap-4 flex-wrap">
                <Button variant="primary" size="large">
                  Hover for Tone & Glow
                </Button>
                <Button variant="primary-elevated" size="large">
                  Hover for Lift & Tone
                </Button>
              </div>
              <p className="text-sm text-gray-600">
                • Primary: Darker gradient tones + shadow glow<br/>
                • Primary Elevated: Darker gradient tones + lift effect + enhanced shadow
              </p>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-700">Secondary & Tertiary</h3>
              <div className="flex gap-4 flex-wrap">
                <Button variant="secondary" size="large">
                  Hover for Color Change
                </Button>
                <Button variant="tertiary" size="large">
                  Hover for Background
                </Button>
              </div>
              <p className="text-sm text-gray-600">
                • Secondary: Gradient shift + shadow enhancement<br/>
                • Tertiary: Background fill with gradient
              </p>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-700">Neutral & Input</h3>
              <div className="flex gap-4 flex-wrap">
                <Button variant="neutral" size="large">
                  Hover for Fill
                </Button>
                <Button variant="input" size="large" fullWidth>
                  Hover for Enhanced Border
                </Button>
              </div>
              <p className="text-sm text-gray-600">
                • Neutral: Background fill + text color change<br/>
                • Input: Background tone shift + border enhancement + shadow
              </p>
            </div>
          </div>
        </section>

        {/* Icon Button Hover Effects */}
        <section className="bg-white rounded-xl p-8 shadow-sm space-y-8">
          <h2 className="text-2xl font-semibold text-gray-800">Icon Button Hover Effects</h2>
          
          <div className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-700">Ghost & Ghost Key</h3>
              <div className="flex gap-4 items-center">
                <IconButton variant="ghost" size="large" icon={<EditIcon />} aria-label="Edit" />
                <IconButton variant="ghost-key" size="large" icon={<HeartIcon />} aria-label="Like" />
                <IconButton variant="ghost" size="medium" icon={<EditIcon />} aria-label="Edit" />
                <IconButton variant="ghost-key" size="medium" icon={<HeartIcon />} aria-label="Like" />
              </div>
              <p className="text-sm text-gray-600">
                • Ghost: Background fill + text color change<br/>
                • Ghost Key: Background fill + color intensify
              </p>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-700">Key & Neutral</h3>
              <div className="flex gap-4 items-center">
                <IconButton variant="key" size="large" icon={<PlusIcon />} aria-label="Add" />
                <IconButton variant="neutral" size="large" icon={<SendIcon />} aria-label="Send" />
                <IconButton variant="key" size="medium" icon={<PlusIcon />} aria-label="Add" />
                <IconButton variant="neutral" size="medium" icon={<SendIcon />} aria-label="Send" />
              </div>
              <p className="text-sm text-gray-600">
                • Key: Background darken + shadow enhancement<br/>
                • Neutral: Background darken + text color intensify
              </p>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-700">Overlay (on colored background)</h3>
              <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-6 rounded-lg">
                <div className="flex gap-4 items-center">
                  <IconButton variant="overlay" size="large" icon={<HeartIcon />} aria-label="Like" />
                  <IconButton variant="overlay" size="medium" icon={<HeartIcon />} aria-label="Like" />
                  <IconButton variant="overlay" size="small" icon={<HeartIcon />} aria-label="Like" />
                </div>
              </div>
              <p className="text-sm text-gray-600">
                • Overlay: Background intensify + blur enhancement + text color change + shadow
              </p>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-700">Ghost Inverse (on dark background)</h3>
              <div className="bg-gray-900 p-6 rounded-lg">
                <div className="flex gap-4 items-center">
                  <IconButton variant="ghost-inverse" size="large" icon={<SendIcon />} aria-label="Send" />
                  <IconButton variant="ghost-inverse" size="medium" icon={<SendIcon />} aria-label="Send" />
                  <IconButton variant="ghost-inverse" size="small" icon={<SendIcon />} aria-label="Send" />
                </div>
              </div>
              <p className="text-sm text-gray-600">
                • Ghost Inverse: White background intensify
              </p>
            </div>
          </div>
        </section>

        {/* Interactive Demo */}
        <section className="bg-white rounded-xl p-8 shadow-sm space-y-8">
          <h2 className="text-2xl font-semibold text-gray-800">Interactive Demo</h2>
          
          <div className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-700">Chat Interface</h3>
              <div className="flex items-end gap-3 p-4 border border-gray-200 rounded-lg bg-gray-50">
                <input
                  type="text"
                  placeholder="Type your message..."
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <IconButton
                  variant="key"
                  size="large"
                  icon={<SendIcon />}
                  aria-label="Send message"
                />
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-700">Action Toolbar</h3>
              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-gray-50">
                <h4 className="text-lg font-medium">Document Title</h4>
                <div className="flex gap-2">
                  <IconButton variant="ghost" size="medium" icon={<EditIcon />} aria-label="Edit" />
                  <IconButton variant="ghost-key" size="medium" icon={<HeartIcon />} aria-label="Like" />
                  <Button variant="primary" size="medium">Save</Button>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-700">Form Buttons</h3>
              <div className="space-y-4">
                <div className="flex gap-3">
                  <Button variant="primary" size="large" fullWidth>
                    Primary Action
                  </Button>
                  <Button variant="secondary" size="large">
                    Secondary
                  </Button>
                </div>
                <Button variant="input" size="large" fullWidth icon={<SendIcon />} iconPosition="right">
                  Ask your question...
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Hover Effects Summary */}
        <section className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-8 shadow-sm text-white">
          <h2 className="text-2xl font-semibold mb-6">Hover Effects Summary</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold mb-3">Regular Buttons</h3>
              <ul className="space-y-2 text-sm">
                <li>• <strong>Background:</strong> Gradient tone shifts and darkening</li>
                <li>• <strong>Shadows:</strong> Enhanced glows and elevations</li>
                <li>• <strong>Movement:</strong> Subtle lift effects (elevated variant)</li>
                <li>• <strong>Performance:</strong> Smooth transitions without size changes</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-3">Icon Buttons</h3>
              <ul className="space-y-2 text-sm">
                <li>• <strong>Background:</strong> Fill transitions and tone changes</li>
                <li>• <strong>Colors:</strong> Text color intensifications and shifts</li>
                <li>• <strong>Effects:</strong> Blur enhancements and shadow changes</li>
                <li>• <strong>Performance:</strong> Smooth transitions focused on color</li>
              </ul>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}